import { useState } from "react"
import { motion, AnimatePresence } from "framer-motion"
import { MessageSquare, Save, ChevronDown, ChevronUp, Dumbbell } from "lucide-react"
import api from "../../api/client"
import type { WorkoutLog } from "../../types"

interface Props {
  logs: WorkoutLog[]
  onNoteUpdated?: (logId: number, notes: string) => void
}

export default function SessionNotes({ logs, onNoteUpdated }: Props) {
  const [expanded, setExpanded] = useState<number | null>(null)
  const [editingId, setEditingId] = useState<number | null>(null)
  const [noteText, setNoteText] = useState("")
  const [saving, setSaving] = useState(false)

  function startEdit(log: WorkoutLog) {
    setEditingId(log.id)
    setNoteText(log.notes ?? "")
    setExpanded(log.id)
  }

  async function saveNote(logId: number) {
    setSaving(true)
    try {
      await api.patch(`/workout_logs/${logId}`, { workout_log: { notes: noteText } })
      onNoteUpdated?.(logId, noteText)
      setEditingId(null)
    } finally {
      setSaving(false)
    }
  }

  if (logs.length === 0) {
    return (
      <div className="flex h-32 items-center justify-center text-muted text-sm">
        Sin sesiones registradas
      </div>
    )
  }

  return (
    <div className="space-y-2">
      {logs.map((log) => {
        const isExpanded = expanded === log.id
        const isEditing = editingId === log.id
        const date = log.performed_at ? new Date(log.performed_at) : null

        return (
          <div
            key={log.id}
            className="rounded-xl border border-white/10 bg-surface-elevated overflow-hidden"
          >
            {/* Header */}
            <button
              className="flex w-full items-center justify-between px-4 py-3 text-left hover:bg-white/5 transition"
              onClick={() => setExpanded(isExpanded ? null : log.id)}
            >
              <div className="flex items-center gap-3">
                <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10">
                  <Dumbbell size={14} className="text-primary" />
                </div>
                <div>
                  <p className="text-sm font-medium text-white">
                    {log.routine?.name ?? "Sesión de entrenamiento"}
                  </p>
                  <p className="text-xs text-muted">
                    {date
                      ? date.toLocaleDateString("es", {
                          day: "2-digit",
                          month: "short",
                          year: "numeric",
                        })
                      : "—"}
                    {log.duration_minutes ? ` · ${log.duration_minutes} min` : ""}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {log.notes && (
                  <span className="flex items-center gap-1 rounded-full bg-primary/10 px-2 py-0.5 text-xs text-primary">
                    <MessageSquare size={10} />
                    Nota
                  </span>
                )}
                {isExpanded ? (
                  <ChevronUp size={14} className="text-muted" />
                ) : (
                  <ChevronDown size={14} className="text-muted" />
                )}
              </div>
            </button>

            {/* Expanded content */}
            <AnimatePresence>
              {isExpanded && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: "auto", opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.2 }}
                  className="border-t border-white/10"
                >
                  <div className="p-4 space-y-3">
                    {isEditing ? (
                      <div className="space-y-2">
                        <textarea
                          value={noteText}
                          onChange={(e) => setNoteText(e.target.value)}
                          placeholder="Escribe el feedback de la sesión..."
                          rows={3}
                          className="w-full rounded-lg border border-white/10 bg-background px-3 py-2 text-sm text-white placeholder-muted/50 focus:border-primary resize-none"
                          autoFocus
                        />
                        <div className="flex gap-2">
                          <button
                            onClick={() => saveNote(log.id)}
                            disabled={saving}
                            className="flex items-center gap-1.5 rounded-lg bg-primary px-3 py-1.5 text-xs font-semibold text-black hover:opacity-90 disabled:opacity-50"
                          >
                            <Save size={12} />
                            {saving ? "Guardando..." : "Guardar"}
                          </button>
                          <button
                            onClick={() => setEditingId(null)}
                            className="rounded-lg border border-white/10 px-3 py-1.5 text-xs text-muted hover:text-white"
                          >
                            Cancelar
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div>
                        {log.notes ? (
                          <p className="text-sm text-white/80 whitespace-pre-wrap">{log.notes}</p>
                        ) : (
                          <p className="text-sm text-muted italic">Sin notas de sesión</p>
                        )}
                        <button
                          onClick={() => startEdit(log)}
                          className="mt-2 flex items-center gap-1.5 text-xs text-primary hover:text-primary/80 transition"
                        >
                          <MessageSquare size={11} />
                          {log.notes ? "Editar nota" : "Agregar nota"}
                        </button>
                      </div>
                    )}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        )
      })}
    </div>
  )
}
